// src/components/FamilyList.js

/**
 * Component to display a list of families and their members,
 * allowing editing/deleting of members and deleting families based on user permissions.
 * This version links member names to their individual detail pages.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faThList, faTh, faHome, faPhone, faGift, faPencilAlt,
    faTrashAlt, faPlus, faFilePdf, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Spinner } from 'react-bootstrap';

import { db, storage, functions } from '../firebase';

import { collection, getDocs, query, where, Timestamp, orderBy } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { format } from 'date-fns';
import { httpsCallable } from 'firebase/functions';

import { useAuth } from '../context/AuthContext';
import MemberPhotoAvatar from './MemberPhotoAvatar'; // Import the reusable component
import FormattedPhoneNumber from './FormattedPhoneNumber'; // Import the FormattedPhoneNumber component

// --- Callable Function References ---
const deleteMemberCallable = httpsCallable(functions, 'deleteMember');
const deleteFamilyCallable = httpsCallable(functions, 'deleteFamily');
const generateDirectoryPdfCallable = httpsCallable(functions, 'generateDirectoryPdf');


// --- Main Component: FamilyList ---
function FamilyList() {
    const { currentUser, isAdmin, userOrgId, userFidClaim, loadingAuth } = useAuth();

    const [families, setFamilies] = useState([]);
    const [allMembers, setAllMembers] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [error, setError] = useState(null);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [pdfMessage, setPdfMessage] = useState({ type: '', text: '' });


    // --- Effect: Fetch Families & Members ---
    useEffect(() => {
        if (loadingAuth || !userOrgId) {
            if (!loadingAuth && currentUser && !userOrgId) {
                setError("Organization details missing from your account. Cannot load families.");
                console.error("FamilyList: OrgId missing from context after auth loaded.");
                setLoadingData(false);
            }
            if (!loadingAuth) {
                setLoadingData(false);
            }
            return;
        }

        setLoadingData(true);
        setError(null);
        let isMounted = true;

        const fetchData = async () => {
            try {
                const familiesQuery = query(collection(db, 'family'), where('orgId', '==', userOrgId), orderBy('surname'));
                const membersQuery = query(collection(db, 'member'), where('orgId', '==', userOrgId)); // Fetches all members for the org

                const [familySnapshot, memberSnapshot] = await Promise.all([
                    getDocs(familiesQuery),
                    getDocs(membersQuery)
                ]);

                if (!isMounted) return;

                const familyListPromises = familySnapshot.docs.map(async (doc) => {
                    const familyData = { id: doc.id, ...doc.data() };
                    let photoURL = '/assets/blank.png';
                    if (familyData.photo) {
                        try {
                            if (familyData.photo.startsWith('http')) {
                                photoURL = familyData.photo;
                            } else {
                                const photoRef = ref(storage, familyData.photo);
                                photoURL = await getDownloadURL(photoRef);
                            }
                        } catch (imageError) {
                            if (imageError.code !== 'storage/object-not-found') {
                                console.error(`Failed to get photo URL for ${familyData.photo}:`, imageError);
                            }
                        }
                    }
                    return { ...familyData, photoURL };
                });
                const resolvedFamilyList = await Promise.all(familyListPromises);

                if (!isMounted) return;
                setFamilies(resolvedFamilyList);

                const resolvedMemberList = memberSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));

                if (!isMounted) return;
                setAllMembers(resolvedMemberList);

            } catch (err) {
                console.error('FamilyList: Error during data fetching:', err);
                if (isMounted) {
                    if (err.code === 'permission-denied') { setError("Permission denied. Check Firestore security rules."); }
                    else if (err.code === 'unauthenticated') { setError("Authentication error during data fetch. Please refresh or log in again."); }
                    else if (err.code === 'failed-precondition' && err.message?.includes('index')) { setError("Database indexing error. Please contact support."); console.error("Firestore requires an index:", err); }
                    else { setError(err.message || "An unknown error occurred while fetching data."); }
                }
            } finally {
                if (isMounted) { setLoadingData(false); }
            }
        };

        fetchData();

        return () => { isMounted = false; };
    }, [userOrgId, loadingAuth, currentUser]);


    // --- Memoized Calculation: Group Members by Family ID ---
    const membersByFamilyId = useMemo(() => {
        const grouped = {};
        allMembers.forEach(member => {
            if (!member || !member.familyId) return;
            if (!grouped[member.familyId]) grouped[member.familyId] = [];
            grouped[member.familyId].push(member);
        });
        Object.values(grouped).forEach(memberList => {
            memberList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        });
        return grouped;
    }, [allMembers]);


    // --- Helper: Format Date ---
    const formatDate = (date) => {
        const formatString = 'MM/dd';
        if (date instanceof Timestamp) {
            try { return format(date.toDate(), formatString); }
            catch (e) { console.error("formatDate Error (Timestamp):", e); return '?'; }
        } else if (typeof date === 'string' && /^\d{2}\/\d{2}$/.test(date)) {
            return date;
        }
        return '';
    };

    // --- Handlers for Delete and PDF ---
    const handleDeleteMember = async (memberIdToDelete, memberName) => {
        if (!window.confirm(`Are you sure you want to permanently delete ${memberName || 'this member'}?`)) return;
        setError(null); setPdfMessage({ type: '', text: '' });
        try {
            const result = await deleteMemberCallable({ memberId: memberIdToDelete });
            if (result?.data?.success) {
                setAllMembers(prevMembers => prevMembers.filter(member => member.id !== memberIdToDelete));
                setPdfMessage({ type: 'success', text: `Member "${memberName}" deleted.` });
                setTimeout(() => setPdfMessage({ type: '', text: '' }), 5000);
            } else {
                setError(result?.data?.message || "Server denied the member deletion request.");
            }
        } catch (err) { setError(`Failed to delete member: ${err.message || "Network/Function error"}`); }
    };
    const handleDeleteFamily = async (familyIdToDelete, familyName) => {
        if (!window.confirm(`REMOVE FAMILY\n\nAre you absolutely sure you want to delete the entire ${familyName || 'this'} family?\n\nWARNING: This will also delete ALL members associated with this family. This action cannot be undone.`)) return;
        setError(null); setPdfMessage({ type: '', text: '' });
        try {
            const result = await deleteFamilyCallable({ familyId: familyIdToDelete });
            if (result?.data?.success) {
                setFamilies(prevFamilies => prevFamilies.filter(family => family.id !== familyIdToDelete));
                setAllMembers(prevMembers => prevMembers.filter(member => member.familyId !== familyIdToDelete));
                setPdfMessage({ type: 'success', text: `Family "${familyName}" deleted.` });
                setTimeout(() => setPdfMessage({ type: '', text: '' }), 5000);
            } else {
                setError(result?.data?.message || "Server denied the family deletion request.");
            }
        } catch (err) { setError(`Failed to delete family: ${err.message || "Network/Function error"}`); }
    };
    const handleGeneratePdf = async () => {
        console.log("Attempting to generate PDF directory...");
        setIsGeneratingPdf(true); setPdfMessage({ type: '', text: '' }); setError(null);
        try {
            const result = await generateDirectoryPdfCallable({});
            const data = result.data;
            if (data?.success && data.filePath) {
                setPdfMessage({ type: 'info', text: 'PDF generated. Preparing download...' });
                const bucketName = data.filePath.split('/')[2];
                const relativePath = data.filePath.substring(`gs://${bucketName}/`.length);
                const storageRef = ref(storage, relativePath);
                const downloadUrl = await getDownloadURL(storageRef);
                const link = document.createElement('a'); link.href = downloadUrl; link.download = 'directory.pdf';
                document.body.appendChild(link); link.click(); document.body.removeChild(link);
                setPdfMessage({ type: 'success', text: 'PDF download started!' }); setTimeout(() => setPdfMessage({ type: '', text: '' }), 5000);
            } else if (data?.success && !data.filePath) {
                setPdfMessage({ type: 'warning', text: 'Directory generation complete, but no families or members were found to include.' });
            } else {
                setPdfMessage({ type: 'danger', text: `Error: ${data?.message || "PDF generation failed on the server."}` });
            }
        } catch (err) { setPdfMessage({ type: 'danger', text: `Error: ${err.message || "Could not generate PDF."}` }); }
        finally { setIsGeneratingPdf(false); }
    };


    // --- Render Logic ---
    if (loadingAuth) {
        return ( <div className="container text-center mt-5" aria-live="polite"> <div className="spinner-border text-primary" role="status"> <span className="visually-hidden">Loading User...</span> </div> </div> );
    }
    if (!currentUser) {
        return ( <div className="container mt-3"> <Alert variant="warning">Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to view families.</Alert> </div> );
    }
    if (loadingData) {
        return ( <div className="container text-center mt-5" aria-live="polite"> <div className="spinner-border text-secondary" role="status"> <span className="visually-hidden">Loading family data...</span> </div> </div> );
    }
    if (error) {
        return ( <div className="container mt-3"> <nav aria-label="breadcrumb" className="mb-3"><ol className="breadcrumb bg-light px-2 py-1 rounded-1 small"> <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li> <li className="breadcrumb-item"><Link to="/families">Families</Link></li> <li className="breadcrumb-item active" aria-current="page">Error</li> </ol></nav> <Alert variant="danger"> <h4>Error Loading Data</h4> <p>{error}</p> </Alert> </div> );
    }

    return (
        <div className="col-md-12">
            <div className="row mx-1 mb-3 align-items-center">
                <div className="col">
                    <nav aria-label="breadcrumb"><ol className="breadcrumb mb-0 bg-light px-2 py-1 rounded-1 small">
                        <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                        <li className="breadcrumb-item"><Link to="/families">Families</Link></li>
                        <li className="breadcrumb-item active" aria-current="page">List View</li>
                    </ol></nav>
                </div>
                <div className="col-auto d-flex align-items-center">
                    {isAdmin && ( <Link to="/families/add" className="btn btn-sm btn-success me-2" title="Add New Family"> <FontAwesomeIcon icon={faPlus} className="me-1" /> <span className="d-none d-md-inline">Add Family</span> </Link> )}
                    {isAdmin && ( <button type="button" className="btn btn-sm btn-secondary me-2" onClick={handleGeneratePdf} disabled={isGeneratingPdf || !userOrgId} title={isGeneratingPdf ? "Generating PDF..." : "Download Directory PDF"} aria-label={isGeneratingPdf ? "Generating directory PDF, please wait" : "Download directory PDF"} > {isGeneratingPdf ? ( <FontAwesomeIcon icon={faSpinner} spin className="me-1" /> ) : ( <FontAwesomeIcon icon={faFilePdf} className="me-1" /> )} <span className="d-none d-md-inline"> {isGeneratingPdf ? "Generating..." : "Download PDF"} </span> </button> )}
                    <span className="text-muted me-2 d-none d-sm-inline small">View:</span>
                    <div className="btn-group btn-group-sm" role="group" aria-label="View toggle">
                        <button type="button" className="btn btn-primary active" disabled aria-current="page" title="List View (Current)"><FontAwesomeIcon icon={faThList} aria-hidden="true" /><span className="visually-hidden">List</span></button>
                        <Link to="/families/grid" className="btn btn-outline-secondary" title="Grid View"><FontAwesomeIcon icon={faTh} aria-hidden="true" /><span className="visually-hidden">Grid</span></Link>
                    </div>
                </div>
            </div>

            {pdfMessage.text && ( <Alert variant={pdfMessage.type || 'info'} className="mx-1" onClose={() => setPdfMessage({ type: '', text: '' })} dismissible> {pdfMessage.text} </Alert> )}

            {families.length === 0 ? (
                <Alert variant="info" className="m-3 text-center">
                    No families found in this organization.
                    {isAdmin && " Use the 'Add Family' button to create one."}
                </Alert>
            ) : (
                families.map((family) => {
                    const familyMembers = membersByFamilyId[family.id] || [];
                    const canEditMembersInThisFamily = isAdmin || (userFidClaim && userFidClaim === family.id);
                    const canDeleteThisFamily = isAdmin || (userFidClaim && userFidClaim === family.id);

                    return (
                        <div className="row" key={family.id}>
                            <div className="col-12 px-md-3 py-2">
                                <div className="card shadow-sm mb-3">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-12 col-md-4 col-lg-3 mb-3 mb-md-0 d-flex flex-column">
                                                <div className="mb-2">
                                                    <Link to={`/families/${family.id}`} className="me-2" style={{ textDecoration: 'underline' }}>
                                                        <h4 className="mb-0 card-title text-primary">{family.surname}</h4>
                                                    </Link>
                                                </div>
                                                <img className="family-list__family-photo-img img-fluid rounded mb-2" src={family.photoURL} alt={`${family.surname || 'Family'} photo`} style={{ width: '100%', height: '180px', objectFit: 'contain', border: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }} loading="lazy" onError={(e) => { if (e.target.src !== '/assets/blank.png') { e.target.onerror = null; e.target.src='/assets/blank.png'; } }} />
                                                <div className="small mt-1 lh-sm flex-grow-1">
                                                    {family.street && <div className="mb-1 d-flex align-items-start"><FontAwesomeIcon icon={faHome} className="me-2 fa-fw text-secondary mt-1" /> <span>{family.street}</span></div>}
                                                    {(family.city || family.state || family.zipcode) && <div className="mb-1 d-flex align-items-start"><span className="fa-fw me-2"></span> <span>{family.city}{family.city && family.state ? ', ' : ''}{family.state} {family.zipcode}</span> </div>}
                                                    {family.phone && <div className="mb-1 d-flex align-items-start"><FontAwesomeIcon icon={faPhone} className="me-2 fa-fw text-secondary mt-1" /> <FormattedPhoneNumber phoneNumber={family.phone} fallback="" /></div>}
                                                    {family.anniversary && <div className="mb-1 d-flex align-items-start"><FontAwesomeIcon icon={faGift} className="me-2 fa-fw text-secondary mt-1" /> <span>Anniv: {formatDate(family.anniversary)}</span></div>}
                                                </div>
                                                <div className="mt-2">
                                                    {canEditMembersInThisFamily && ( <Link to="/members/add" state={{ preselectedFamilyId: family.id, preselectedFamilyName: family.surname }} className="btn btn-sm btn-success py-0 px-2 d-block w-100 mb-1"> <FontAwesomeIcon icon={faPlus} className="me-1" /> <small>Add Member</small> </Link> )}
                                                    {canDeleteThisFamily && ( <button type="button" className="btn btn-sm btn-danger py-0 px-1 d-block w-100" title={`Remove ${family.surname} Family`} onClick={() => handleDeleteFamily(family.id, family.surname)} > <FontAwesomeIcon icon={faTrashAlt} fixedWidth className="me-1" /> <small>Remove Family</small> </button> )}
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-8 col-lg-9">
                                                <div className="table-responsive">
                                                    <table className="table table-bordered table-hover table-sm mb-0 align-middle small">
                                                        <thead className='table-light'>
                                                            <tr>
                                                                <th style={{width: '50px', textAlign: 'center'}}></th>
                                                                <th style={{minWidth: '120px'}}>Name</th>
                                                                <th style={{minWidth: '70px'}}>Birthday</th>
                                                                <th style={{minWidth: '110px'}}>Cell</th>
                                                                <th>Email</th>
                                                                <th className="text-center" style={{width: '80px'}}>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <MemberList
                                                                familyId={family.id}
                                                                familyMembers={familyMembers}
                                                                formatDate={formatDate}
                                                                canEdit={canEditMembersInThisFamily}
                                                                onDeleteMember={handleDeleteMember}
                                                            />
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );
}

// --- MemberList Component (Child - Renders Member Table Rows) ---
const MemberList = React.memo(({ familyId, familyMembers, formatDate, canEdit, onDeleteMember }) => {
    if (!familyMembers || familyMembers.length === 0) {
        return <tr><td colSpan="6" className="text-center text-muted fst-italic py-3">No members listed for this family.</td></tr>;
    }

    return (
        <>
            {familyMembers.map(member => {
                // Defensive check for required IDs
                if (!member || !member.id || !familyId) {
                    console.warn(`MemberList: Skipping rendering row for invalid data:`, { member, familyId });
                    return null;
                }

                let birthdayValueToFormat = null;
                if (member.birthday && member.birthday instanceof Timestamp) {
                    birthdayValueToFormat = member.birthday;
                } else {
                    birthdayValueToFormat = typeof member.birthday === 'string' ? member.birthday : null;
                }

                const photoCacheKey = member.imageUpdatedAt instanceof Timestamp
                                        ? member.imageUpdatedAt.toMillis().toString()
                                        : (member.imageUpdatedAt || member.id);

                return (
                    <tr key={member.id}>
                        <td className="text-center">
                            <MemberPhotoAvatar
                                src={member.photo}
                                alt={member.name || 'Member'}
                                size={30}
                                cacheKey={photoCacheKey}
                            />
                        </td>
                        <td className="text-nowrap">
                            <Link to={`/families/${familyId}/member/${member.id}`}>
                                {member.name || '-'}
                            </Link>
                        </td>
                        <td className="text-nowrap">{formatDate(birthdayValueToFormat)}</td>
                        <td className="text-nowrap">
                            <FormattedPhoneNumber phoneNumber={member.cellphone} fallback="-" />
                        </td>
                        <td style={{ wordBreak: 'break-all' }}>{member.email || '-'}</td>
                        <td className="text-center text-nowrap">
                            {canEdit ? (
                                <>
                                    <Link
                                        to={`/members/edit/${member.id}`}
                                        className="btn btn-sm btn-outline-secondary py-0 px-1"
                                        title={`Edit ${member.name || 'Member'}`}
                                        aria-label={`Edit ${member.name || 'Member'}`} >
                                        <FontAwesomeIcon icon={faPencilAlt} fixedWidth />
                                    </Link>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger py-0 px-1 ms-1"
                                        title={`Delete ${member.name || 'Member'}`}
                                        aria-label={`Delete ${member.name || 'Member'}`}
                                        onClick={() => onDeleteMember(member.id, member.name)}
                                    >
                                        <FontAwesomeIcon icon={faTrashAlt} fixedWidth />
                                    </button>
                                </>
                            ) : (
                                <span className="text-muted small" title="Actions unavailable">--</span>
                            )}
                        </td>
                    </tr>
                );
            })}
        </>
    );
});

export default FamilyList;
